import React, { useEffect, useState, useLocation } from 'react';
import { LinearProgress, Paper, TableBody, Box, Typography, Button, Tabs, Tab, TableContainer, TableHead, TableRow, TableCell, Table } from '@mui/material';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { GoogleOAuthProvider } from '@react-oauth/google';
import LoginButton from './LoginButton';
import GoogleCampaignDialog from './GoogleCampaignDialog';
import GoogleConversionActionDialog from './GoogleConversionActionDialog';
import GoogleCustomerIdsDropdown from './GoogleCustomerIDsDropDown';
import { disconnectFromGoogle } from '../../services/integrations';
import { getCustomerIds, getCampaigns, getUserData, createCampaign, updateCampaign, deleteCampaign, getConversionActions, createConversionAction, updateConversionAction, deleteConversionAction } from '../../services/google';

function GoogleIntegration({ user, setUser }) {

	const [isLoadingUser, setIsLoadingUser] = useState(false);
	const [customerIds, setCustomerIds] = useState([]);
	const [error, setError] = useState('');
	const [selectedCustomerIdIndex, setSelectedCustomerIdIndex] = useState(null);
	const [selectedUserData, setSelectedUserData] = useState(null);
	const [selectedAccount, setSelectedAccount] = useState(null);
	const [campaigns, setCampaigns] = useState(null);
	const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
	const [selectedTab, setTab] = React.useState(0);
	const [showNewCampaignDialog, setShowNewCampaignDialog] = useState(false);
	const [showNewConversionActionDialog, setShowNewConversionActionDialog] = useState(false);
	const [editingCampaign, setEditingCampaign] = useState(null);
	const [editingConversionAction, setEditingConversionAction] = useState(null);
	const [conversionActions, setConversionActions] = useState(null);
	const [isConnected, setIsConnected] = useState(user.google && user.google.tokens && user.google.tokens.access_token ? true : false);
	const [isLoadingGoogleConnection, setIsLoadingGoogleConnection] = useState(false);


	const handleTabChange = (event, newValue) => {
	  setTab(newValue);
	  setSelectedAccount(selectedUserData.adAccounts[newValue]);
	};

	const handleCustomerIdChange = async (event, newValue) => {
		setError("");
        setSelectedCustomerIdIndex(newValue);

		try {
			const { data } = await getUserData(user._id, customerIds[newValue])
			setSelectedUserData(data);
			setSelectedAccount(data.adAccounts[0]);
		} catch ({ message }) {
			setSelectedUserData(null);
			setSelectedAccount(null);
			setError(message);
		}
    };

	const upsertCampaign = async (campaignData, isUpdating) => {
		setError("");
		try {
			if (isUpdating) {
				await updateCampaign(user._id, customerIds[selectedCustomerIdIndex], selectedAccount.customer_client.id, campaignData)
			} else {
				await createCampaign(user._id, customerIds[selectedCustomerIdIndex], selectedAccount.customer_client.id, campaignData)
			}
			await fetchCampaigns();	
		} catch (error) {
			setError("error");
			console.error('Error creating/updating google campaign:', error);
		} finally { }
	}

	const upsertConversionAction = async (conversionActionData, isUpdating) => {
		if (selectedAccount === undefined || selectedAccount?.customer_client === undefined) { return }
		setError("");
		try {
			if (isUpdating) {
				await updateConversionAction(user._id, customerIds[selectedCustomerIdIndex], selectedAccount.customer_client?.id, conversionActionData.name, conversionActionData.resource_name)
			} else {
				await createConversionAction(user._id, customerIds[selectedCustomerIdIndex], selectedAccount.customer_client?.id, conversionActionData.name)
			}
			await fetchConversionActions();	
		} catch (error) {
			setError("error");
			console.error('Error creating/updating google conversion action:', error);
		}
	}

	const handleDeleteCampaign = async (campaignData) => {
		setError("");
		try {
			await deleteCampaign(user._id, customerIds[selectedCustomerIdIndex], selectedAccount.customer_client.id, campaignData)
			await fetchCampaigns();
		} catch (error) {
			setError("error");
			console.error('Error deleting google campaign:', error);
		}
	}

	const handleDeleteConversionAction = async ({ conversion_action }) => {
		setError("");
		try {
			await deleteConversionAction(user._id, customerIds[selectedCustomerIdIndex], selectedAccount.customer_client.id, conversion_action.resource_name)
			await fetchConversionActions();
		} catch (error) {
			setError("error");
			console.error('Error deleting google campaign:', error);
		}
	}

	const connect = ({ data }) => {
		setUser(data);
		setIsConnected(data.google && data.google.tokens && data.google.tokens.access_token ? true : false);
	};

	const disconnect = async () => {  
		setIsLoadingGoogleConnection(true);		
		try {
			const { updatedUser } = await disconnectFromGoogle(user)
			console.log("disconnectFromGoogle response", updatedUser);
			if (updatedUser.message) {
				setError(updatedUser.message);
				return;
			} else {
				setUser(updatedUser);
				setIsConnected(false);
			}
		} catch (error) {
			setError("Erro ao desconectar do Google. Tente novamente.");
			console.error('Error disconnecting from Google:', error);
		} finally {
			setIsLoadingGoogleConnection(false);
		}
	}

	const getUserIDs = async () => {
		setError("");
		setIsLoadingUser(true);
		try {
			const { data } = await getCustomerIds(user._id)
			setCustomerIds(data.customerIds)
		} catch (error) {
			console.error('Error fetching google customer IDs data:', error);
		} finally {
			setIsLoadingUser(false);
		}
	}

	const fetchConversionActions = async () => {
		if (selectedAccount === undefined || selectedAccount?.customer_client === undefined) { return }
		setError("");
		setConversionActions(null);
		try {
			const { data } = await getConversionActions(user._id, customerIds[selectedCustomerIdIndex], selectedAccount.customer_client?.id)
			setConversionActions(data.conversionActions)
			console.log("getConversionActions", data.conversionActions);
		} catch (error) {
			console.error('Error fetching google conversion actions:', error);
		}
	}

	const fetchCampaigns = async () => {
		if (selectedAccount === undefined || selectedAccount?.customer_client === undefined) { return }
		setIsLoadingCampaigns(true)
		setCampaigns(null);
		try {
			const { data } = await getCampaigns(user._id, customerIds[selectedCustomerIdIndex], selectedAccount.customer_client.id)
			setCampaigns(data.campaigns);
			console.log("getCampaigns data", data);
		} catch (error) {
			console.error('Error fetching google campaigns for customerId:', error);
		} finally {
			setIsLoadingCampaigns(false);
		}
	}

	useEffect(() => {
		fetchCampaigns();
		fetchConversionActions();
	}, [selectedAccount]);

	useEffect(() => {
		if (user) {
		  console.log("User updated, calling getUserIDs");
		  getUserIDs();
		  setIsConnected(user.google && user.google.tokens && user.google.tokens.access_token ? true : false);
		}
	  }, [user]);

	const content = () => {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%'}}>
				<Box>
					<Typography variant="subtitle1" fontWeight="bold" style={{ lineHeight: "24px" }}>
						Você está conectado com sua conta do Google Ads!
					</Typography>
					<Typography variant="subtitle2" marginTop={2}>
						💻 Todos os seus clicks que tiverem um <strong>campaignId</strong> associado também irão trazer o nome da campanha nos seus relatórios!
					</Typography>
					<Typography variant="subtitle2" marginTop={2}>
						📊 Em breve, poderá gerenciar suas campanhas e ações de conversão diretamente do nosso painel!
					</Typography>
				</Box>
				<Box sx={{ display: 'flex', justifyContent: 'left', marginTop: 2 }}>
					<Button variant="contained" onClick={disconnect}>Desconectar</Button>
				</Box>
				{error && <Typography variant="h6" color="error">{error}</Typography>}
			</Box>
		);
	}

	{/* Google detailed information. work in progress */ }
	const googleDetailedInfo = () => {
		return (
			<Box
				_id="container"
				sx={{
					paddingTop: 2,
					width: '100%',
					height: '100vh',
					boxSizing: 'border-box'
				}}>
				<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
				{/* CustomerIds List (Drop Down) */}
				{ isLoadingUser 
				? <LinearProgress sx={{ marginTop: 4, marginRight: 3 }}/> 
				: error 
				? <Box>
					<Typography variant="h6">{error}</Typography>
					<Button onClick={getUserIDs}>Tentar Novamente</Button>
					<Button onClick={disconnect}>Deslogar</Button>
				  </Box>
				: <GoogleCustomerIdsDropdown 
					customerIds={customerIds} 
					selectedCustomerIdIndex={selectedCustomerIdIndex} 
					handleCustomerIdChange={handleCustomerIdChange} 
				/>
			}

				{/* Content Section */}
				{selectedUserData && (
					<Box sx={{ flexGrow: 1 }}>
						<Tabs value={selectedTab} onChange={handleTabChange} aria-label="basic tabs example">
						{[...selectedUserData.adAccounts, ...selectedUserData.managerAccounts].map((account, index) => (
							<Tab key={index} label={`${account.customer_client.descriptive_name} (${account.customer_client.id})`} />
						))}
						</Tabs>
					</Box>
					)
				}
				</Box>

				{/* New Campaign Dialog */}	
				<GoogleCampaignDialog 
					editingCampaign={editingCampaign}
					open={showNewCampaignDialog} 
					onClose={() => {
						setShowNewCampaignDialog(false)
						setEditingCampaign(null)
					}} 
					onCreate={(campaignData) => upsertCampaign(campaignData, editingCampaign !== null)}/>

				{/* New Conversion Action Dialog */}	
				<GoogleConversionActionDialog 
					editingConversionAction={editingConversionAction}
					open={showNewConversionActionDialog} 
					onClose={() => setShowNewConversionActionDialog(false)} 
					onCreate={(conversionActionData) => upsertConversionAction(conversionActionData, editingConversionAction !== null)}/>

				{/* Campaigns List */}
				{ isLoadingCampaigns 
				? <LinearProgress sx={{ marginTop: 4, marginRight: 3 }}/> 
				: campaigns &&
					<Box sx={{ marginTop: 2, marginRight: 3 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
						<Typography variant="h6">
							Campanhas
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setShowNewCampaignDialog(true)}
							sx={{ marginLeft: 2 }}>
							Criar Nova
						</Button>
						</Box>
						<TableContainer component={Paper}>
							<Table sx={{ minWidth: 650 }} aria-label="simple table">
							<TableHead>
								<TableRow>
								<TableCell>Campanha</TableCell>
								<TableCell align="right">Conversões</TableCell>
								<TableCell align="right">Cliques</TableCell>
								<TableCell align="right">Custo</TableCell>
								<TableCell align="right">Impressões</TableCell>
								<TableCell align="right">Budget</TableCell>
								<TableCell align="right">Ações</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{campaigns.map((row, index) => (
								<TableRow
									key={row?.campaign?.id || index} // Use fallback if id is not defined
									sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
								>
									<TableCell component="th" scope="row">
									{row?.campaign?.name || 'N/A'}
									</TableCell>
									<TableCell align="right">{row?.metrics?.all_conversions ?? 'N/A'}</TableCell>
									<TableCell align="right">{row?.metrics?.clicks ?? 'N/A'}</TableCell>
									<TableCell align="right">{row?.metrics?.cost_micros ?? 'N/A'}</TableCell>
									<TableCell align="right">{row?.metrics?.impressions ?? 'N/A'}</TableCell>
									<TableCell align="right">{`$${(row?.campaign_budget.amount_micros / 1_000_000).toFixed(2)}` ?? 'N/A'}</TableCell>
									<TableCell align="right">
									<IconButton onClick={() => { 
										setEditingCampaign(row)  
										console.log("Edit clicked", row)
										setShowNewCampaignDialog(true)
									}} aria-label="edit">
										<EditIcon />
									</IconButton>
									<IconButton onClick={() => { 
										if (window.confirm("Deseja realmente deletar esta campanha?")) {
											handleDeleteCampaign(row);
										}
									 }} color="error" aria-label="delete">
										<DeleteIcon />
									</IconButton>
									</TableCell>
								</TableRow>
								))}
							</TableBody>
							</Table>
						</TableContainer>
					</Box>
				}

				{/* Conversion Actions List */}
				{ conversionActions &&
					<Box sx={{ marginTop: 2, marginRight: 3 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
						<Typography variant="h6">
							Ações de Conversão
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => {
								setEditingConversionAction(null)
								setShowNewConversionActionDialog(true)
							}}
							sx={{ marginLeft: 2 }}>
							Criar Nova
						</Button>
						</Box>
						<TableContainer component={Paper}>
							<Table sx={{ minWidth: 650 }} aria-label="simple table">
							<TableHead>
								<TableRow>
								<TableCell>Ação de Conversão</TableCell>
								<TableCell align="left">ID</TableCell>
								<TableCell align="right">Status</TableCell>
								<TableCell align="right">Tipo</TableCell>
								<TableCell align="right">Categoria</TableCell>
								<TableCell align="right">Ações</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{conversionActions.map((row, index) => (
								<TableRow
									key={row?.conversion_action.id || index} // Use fallback if id is not defined
									sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
								>
									<TableCell component="th" scope="row"> {row?.conversion_action.name || 'N/A'}</TableCell>
									<TableCell align="left">{row?.conversion_action.resource_name ?? 'N/A'}</TableCell>
									<TableCell align="right">{row?.conversion_action.status ?? 'N/A'}</TableCell>
									<TableCell align="right">{row?.conversion_action.type ?? 'N/A'}</TableCell>
									<TableCell align="right">{row?.conversion_action.category ?? 'N/A'}</TableCell>
									<TableCell align="right">
									<IconButton onClick={() => { 
										setEditingConversionAction(row)  
										console.log("Edit clicked", row)
										setShowNewConversionActionDialog(true)
									}} aria-label="edit">
										<EditIcon />
									</IconButton>
									<IconButton onClick={() => { 
										if (window.confirm("Deseja realmente deletar esta ação de conversão?")) {
											handleDeleteConversionAction(row);
										}
									 }} color="error" aria-label="delete">
										<DeleteIcon />
									</IconButton>
									</TableCell>
								</TableRow>
								))}
							</TableBody>
							</Table>
						</TableContainer>
					</Box>
				}
			</Box>
		)
	}

	return (
		isConnected
		? content()
		: 
		<Box 
			sx={{ 
				marginTop: 2,
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
				paddingLeft: 1,
				paddingRight: 1,
			}}
		>
			<Box>
				<Typography variant="subtitle1" fontWeight="bold" style={{ lineHeight: "24px" }}>
					Contecte à sua conta do Google Ads para:
				</Typography>
				<Typography variant="subtitle2" marginTop={2}>
					📊 Criar e gerenciar campanhas
				</Typography>
				<Typography variant="subtitle2">
					💵 Criar e gerenciar ações de conversão
				</Typography>
				<Typography variant="subtitle2">
					🔎 Obter informações detalhadas sobre suas campanhas
				</Typography>
			</Box>
			
			<Box
				sx={{ 
					marginTop: 2,
					width: '100%',
				}}>
				<GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
					<LoginButton user={user} onLogin={connect} />
				</GoogleOAuthProvider>
				{isLoadingGoogleConnection && <LinearProgress sx={{ marginTop: 4, marginRight: 3 }}/> }
			</Box>
		</Box>
	);
}

export default GoogleIntegration;